import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { API } from "../lib/api";

/**
 * useAppVersion — polls `/api/version` on an interval and shows a sticky
 * "Update available" toast when the backend reports a new build.
 *
 * Why this exists: the app is served as a CRA bundle behind a CDN. After a
 * deploy, returning users can stay on the old JS/CSS until they hard-refresh.
 * This hook detects a fresh backend deploy (the version string is regenerated
 * on every backend boot) and prompts the user to reload — instant cache bust
 * without forcing a service worker.
 *
 * Behaviour:
 *  - First load: stores the version. No toast.
 *  - Every `intervalMs` (default 60s): re-fetches.
 *  - If the version changes: shows a non-dismissible toast with a "Reload" CTA
 *    that does a hard reload (`location.reload()`).
 *  - Polling pauses while the tab is hidden and resumes on visibility change.
 *  - All network errors are swallowed — we never want this to bother the user.
 */
export default function useAppVersion({ intervalMs = 60_000 } = {}) {
  const initialVersion = useRef(null);
  const toastShown = useRef(false);

  useEffect(() => {
    const url = `${API}/version`;

    let timer = null;

    const showUpdateToast = () => {
      if (toastShown.current) return;
      toastShown.current = true;
      toast(
        "A new version of Care for Flora is available",
        {
          description: "Reload to get the latest fixes and features.",
          duration: Infinity,
          action: {
            label: "Reload",
            onClick: () => {
              // Hard reload — bypasses the disk cache.
              window.location.reload();
            },
          },
          id: "app-update-available",
        }
      );
    };

    const check = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        const v = data?.version;
        if (!v) return;
        if (initialVersion.current == null) {
          initialVersion.current = v;
        } else if (v !== initialVersion.current) {
          showUpdateToast();
        }
      } catch {
        /* network blip — ignore */
      }
    };

    // Initial check happens immediately so we record a baseline.
    check();
    timer = setInterval(check, intervalMs);

    // Re-check the moment the tab regains focus so users on a stale tab get
    // the prompt without waiting a full interval.
    const onVisible = () => {
      if (!document.hidden) check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs]);
}
